import React, { useState } from 'react';
import {
    Container,
    Button,
    Navbar,
    NavbarBrand
} from 'reactstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import {
    FaArrowLeft,
    FaHome,
    FaUserCircle,
    FaSignOutAlt,
    FaBars,
    FaTimes,
    FaChevronDown,
    FaChevronRight
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import { authActions } from '../sagas/authSlice';
import logo from '../assets/images/logoh.png';
import './ModuleLayout.css';

const ModuleLayout = ({ children, moduleConfig, breadcrumbs }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { fullName, businessName } = useSelector(state => state.auth);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [openSubMenu, setOpenSubMenu] = useState(null);

    const ModuleIcon = moduleConfig?.icon || FaHome;
    const moduleAccent = moduleConfig?.color || '#2563eb';

    const isPathActive = (path) => path && (location.pathname === path || location.pathname.startsWith(`${path}/`));
    const isSubMenuActive = (item) => item.subMenu?.some((subItem) => isPathActive(subItem.path));

    const handleLogout = () => {
        Swal.fire({
            title: t('auth.logoutConfirmTitle'),
            text: t('auth.logoutConfirmText'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: t('common.confirm'),
            cancelButtonText: t('common.cancel')
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(authActions.logout());
                Swal.fire({
                    icon: 'success',
                    title: t('auth.logoutSuccess'),
                    showConfirmButton: false,
                    timer: 1500
                }).then(() => {
                    navigate('/login');
                });
            }
        });
    };

    const handleBackToModules = () => {
        localStorage.removeItem('selectedModule');
        navigate('/modules');
    };

    return (
        <div className="module-layout-wrapper" style={{ '--module-accent': moduleAccent }}>
            <div className="module-header-wrapper">
                <Navbar className="module-navbar" expand="lg">
                    <Container fluid className="module-navbar-inner">
                        <div className="module-navbar-main">
                            <div className="module-navbar-left">
                                <Button
                                    color="link"
                                    className="sidebar-toggle-btn"
                                    onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                                    title={sidebarCollapsed ? t('common.open', 'Ouvrir') : t('common.close', 'Fermer')}
                                >
                                    {sidebarCollapsed ? <FaBars size={16} /> : <FaTimes size={16} />}
                                </Button>

                                <NavbarBrand className="module-brand">
                                    <img src={logo} alt="MatjarPos" className="module-brand-logo" />
                                    <div className="brand-info">
                                        <div className="business-name">{businessName || 'MatjarPos'}</div>
                                        <div className="module-indicator">
                                            <ModuleIcon size={13} />
                                            <span className="module-name">{moduleConfig?.title}</span>
                                        </div>
                                    </div>
                                </NavbarBrand>
                            </div>

                            <div className="module-navbar-right">
                                <div className="module-user-chip">
                                    <FaUserCircle size={18} />
                                    <span>{fullName}</span>
                                </div>
                                <Button
                                    color="light"
                                    size="sm"
                                    className="logout-btn"
                                    onClick={handleLogout}
                                    title={t('nav.signOut')}
                                >
                                    <FaSignOutAlt size={12} />
                                </Button>
                            </div>
                        </div>

                        <div className="module-navbar-meta">
                            <nav className="breadcrumb-nav">
                                <span className="breadcrumb-item" onClick={handleBackToModules}>
                                    Modules
                                </span>
                                <span className="breadcrumb-separator">/</span>
                                <span className="breadcrumb-item" onClick={() => navigate('')}>
                                    {moduleConfig?.title}
                                </span>

                                {breadcrumbs?.map((breadcrumb, index) => (
                                    <React.Fragment key={index}>
                                        <span className="breadcrumb-separator">/</span>
                                        {breadcrumb.href ? (
                                            <span
                                                className="breadcrumb-item"
                                                onClick={() => navigate(breadcrumb.href)}
                                            >
                                                {breadcrumb.label}
                                            </span>
                                        ) : (
                                            <span className="breadcrumb-current">
                                                {breadcrumb.label}
                                            </span>
                                        )}
                                    </React.Fragment>
                                ))}
                            </nav>

                            <small className="module-date">
                                {new Date().toLocaleDateString('fr-FR', {
                                    weekday: 'short',
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric'
                                })}
                            </small>
                        </div>
                    </Container>
                </Navbar>
            </div>

            <div className="module-content-wrapper">
                <aside className={`module-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`} data-module={moduleConfig?.id}>
                    <div className="sidebar-header">
                        <div className="sidebar-module-card">
                            <div className="sidebar-module-icon">
                                <ModuleIcon size={22} />
                            </div>
                            <div className="sidebar-module-text">
                                {!sidebarCollapsed && (
                                    <>
                                        <h6>{moduleConfig?.title}</h6>
                                        <small>{moduleConfig?.description}</small>
                                    </>
                                )}
                            </div>
                            {!sidebarCollapsed && (
                                <Button
                                    color="link"
                                    size="sm"
                                    className="sidebar-collapse-btn"
                                    onClick={() => setSidebarCollapsed(true)}
                                    title={t('common.close', 'Fermer')}
                                >
                                    <FaTimes size={16} />
                                </Button>
                            )}
                        </div>
                    </div>

                    <div className="sidebar-menu">
                        <button className="sidebar-menu-item sidebar-back-item" type="button" onClick={handleBackToModules}>
                            <FaArrowLeft size={16} />
                            {!sidebarCollapsed && <span>{t('navigation.backToModules')}</span>}
                        </button>

                        {moduleConfig?.menuItems?.map((item, idx) => (
                            item.subMenu ? (
                                <div key={item.label || idx} className="sidebar-menu-group">
                                    <button
                                        type="button"
                                        className={`sidebar-menu-item ${openSubMenu === item.label || isSubMenuActive(item) ? 'active' : ''}`}
                                        onClick={() => setOpenSubMenu(openSubMenu === item.label ? null : item.label)}
                                    >
                                        <span className="sidebar-menu-label">
                                            <item.icon size={16} />
                                            {!sidebarCollapsed && <span>{item.label}</span>}
                                        </span>
                                        {!sidebarCollapsed && (
                                            openSubMenu === item.label ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />
                                        )}
                                    </button>

                                    {(openSubMenu === item.label || isSubMenuActive(item)) && !sidebarCollapsed && (
                                        <div className="sidebar-submenu">
                                            {item.subMenu.map((subItem, subIdx) => (
                                                <button
                                                    type="button"
                                                    key={subItem.path || subIdx}
                                                    className={`sidebar-submenu-item ${isPathActive(subItem.path) ? 'active' : ''}`}
                                                    onClick={() => navigate(subItem.path)}
                                                >
                                                    <subItem.icon size={14} />
                                                    <span>{subItem.label}</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    key={item.path || idx}
                                    className={`sidebar-menu-item ${isPathActive(item.path) ? 'active' : ''}`}
                                    onClick={() => navigate(item.path)}
                                >
                                    <span className="sidebar-menu-label">
                                        <item.icon size={16} />
                                        {!sidebarCollapsed && <span>{item.label}</span>}
                                    </span>
                                </button>
                            )
                        ))}
                    </div>
                </aside>

                <main className="module-main-content">
                    <Container fluid className="module-main-container">
                        {children}
                    </Container>
                </main>
            </div>
        </div>
    );
};

export default ModuleLayout;
